import React, { FC, useState } from 'react'

import { Item, Step } from 'modules/processes/domain'
import { ItemType } from 'modules/processes/hooks/types'
import { MaterializedItemsFormValues, useItemForm } from 'modules/processes/hooks/useWizardItemForm'
import { itemTaskType } from 'modules/processes/lib/taskTypeConverter'

import type ItemForm from 'modules/processes/components/ItemWizard/components/Form/ItemForm'
import { useItemProvider } from './item'

interface FormProviderContextProps {
  currentStep: Step
  associatedTaskItem?: Item
  originalItem?: Item
  form: ItemForm
  currentTaskType?: string

  setInitialFormValue: (value: Record<string, unknown>) => void
  changeValue: <T extends keyof MaterializedItemsFormValues>(
    key: T,
    value: MaterializedItemsFormValues[T]
  ) => void
  initializeForm: (itemType: ItemType, values?: Record<string, unknown>) => ItemForm
  multiselectOpened: boolean
  setMultiselectOpened: (value: boolean) => void
  setDueDateOpen: React.Dispatch<React.SetStateAction<boolean>>
  dueDateOpen: boolean
}

const FormProviderContext = React.createContext<FormProviderContextProps | null>(null)

interface FormProviderProps {
  item?: Item
  stepId: number
  currentStep: Step
  children: React.ReactNode
}

export const FormProvider: FC<FormProviderProps> = ({ item, stepId, currentStep, children }) => {
  const { form, changeValue, setForm } = useItemForm({ item, stepId, currentStep })
  // Remove this when the conflict between wizard and multiselect is fixed
  const [multiselectOpened, setMultiselectOpened] = useState<boolean>(false)
  const [dueDateOpen, setDueDateOpen] = useState<boolean>(
    Boolean(Number.isInteger(item?.offsetAmount) && item?.offsetDirection)
  )

  const { itemType } = useItemProvider()

  const setInitialFormValue = (value: Record<string, unknown>) => {
    const objKeys = Object.keys(value)

    objKeys.forEach((key) => {
      const objValue = value[key]

      if ([undefined, ''].includes(form.get(key).value)) {
        form.setValues({ [key]: objValue })
      }
    })
  }

  // We'll return the current item if it's the same type as the selected itemType
  const filteredItem = () => {
    if (item && itemTaskType(item) === itemType) {
      return item
    }

    if (
      item &&
      item.configurationType === 'Processes::Action' &&
      item.configurationIdentifier === itemType
    ) {
      return item
    }
  }

  const currentTaskType = item?.taskType ? itemTaskType(item) : itemType

  return (
    <FormProviderContext.Provider
      value={{
        currentStep,
        associatedTaskItem: filteredItem(),
        originalItem: item,
        currentTaskType,
        form,
        setInitialFormValue,
        changeValue,
        initializeForm: setForm,
        multiselectOpened,
        setMultiselectOpened,
        setDueDateOpen,
        dueDateOpen,
      }}
    >
      {children}
    </FormProviderContext.Provider>
  )
}

export const useFormProvider = (): FormProviderContextProps => {
  const FormProviderModule = React.useContext(FormProviderContext)

  if (!FormProviderModule)
    throw new Error('useFormProvider must be used within an FormProviderModule')

  return FormProviderModule
}
